import React, { useState, useRef, useEffect } from 'react';
import { Bell, Users, Check, Sparkles, X, Trash2, Loader, CalendarDays } from 'lucide-react';
import notificationsService from '../../services/notificationsService';
import teamsService from '../../services/teamsService';
import { useNotificationStore } from '../../store/notificationStore';
import OsScroll from './OsScroll';
import AppointmentInviteActions from './AppointmentInviteActions';

function formatTime(timestamp) {
  const s = Math.floor((Date.now() - new Date(timestamp)) / 1000);
  if (s < 60) return 'Just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return new Date(timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function NotifIcon({ type }) {
  let Icon = Bell;
  let bg = 'rgba(var(--accent-rgb), 0.85)';

  if (type === 'team_invitation') { Icon = Users; }
  else if (type && type.startsWith('appointment_')) { Icon = CalendarDays; }
  else if (type === 'workspace_shared' || type === 'collection_shared' || type === 'request_shared') {
    Icon = Check;
  } else if (type === 'workspace_updated') {
    Icon = Sparkles;
    bg = 'rgba(100,100,120,0.55)';
  }

  return (
    <div style={{
      width: 28, height: 28, borderRadius: 8, flexShrink: 0,
      background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon size={13} style={{ color: '#fff' }} />
    </div>
  );
}

function useDark() {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
  React.useEffect(() => {
    const obs = new MutationObserver(() => setIsDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

const NotificationsModal = ({
  isOpen,
  onClose,
  notifications,
  setNotifications,
  setUnreadCount,
  onInviteAction,
}) => {
  const isDark = useDark();
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const selectAllRef = useRef(null);
  const { success: toastSuccess, error: toastError, info: toastInfo } = useNotificationStore.getState();

  // Reset selection when modal closes or notifications change
  useEffect(() => {
    if (!isOpen) setSelected(new Set());
  }, [isOpen]);

  // Keep select-all checkbox indeterminate state in sync
  useEffect(() => {
    if (!selectAllRef.current) return;
    const count = notifications.length;
    const sel = selected.size;
    selectAllRef.current.indeterminate = sel > 0 && sel < count;
  }, [selected, notifications]);

  if (!isOpen) return null;

  const allSelected = notifications.length > 0 && selected.size === notifications.length;
  const hasRead = notifications.some(n => n.is_read);
  const hasSelection = selected.size > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(notifications.map(n => n.id)));
    }
  };

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    const ids = [...selected];
    try {
      await Promise.all(ids.map(id => notificationsService.deleteNotification(id)));
      const removedUnread = notifications.filter(n => ids.includes(n.id) && !n.is_read).length;
      setNotifications(prev => prev.filter(n => !ids.includes(n.id)));
      if (removedUnread > 0) setUnreadCount(prev => Math.max(0, prev - removedUnread));
      setSelected(new Set());
    } catch {
      toastError('Failed to delete notifications');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleClearRead = async () => {
    setClearing(true);
    try {
      await notificationsService.clearReadNotifications();
      setNotifications(prev => prev.filter(n => !n.is_read));
      setSelected(prev => {
        const next = new Set(prev);
        notifications.filter(n => n.is_read).forEach(n => next.delete(n.id));
        return next;
      });
      toastInfo('Read notifications cleared');
    } catch {
      toastError('Failed to clear read notifications');
    } finally {
      setClearing(false);
    }
  };

  const handleInvite = async (notification, action) => {
    try {
      const token = notification.data?.invitation_token;
      if (!token) return;
      if (action === 'accept') {
        const res = await teamsService.acceptInvitation(token);
        toastSuccess(`Joined "${res.team.name}"!`);
        onInviteAction?.(res.team);
      } else {
        await teamsService.declineInvitation(token);
        toastInfo('Invitation declined');
      }
      setNotifications(prev => prev.map(n =>
        n.id === notification.id
          ? { ...n, is_read: true, data: { ...n.data, invitation_status: action === 'accept' ? 'accepted' : 'declined' } }
          : n
      ));
      if (!notification.is_read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      toastError(err.message || 'Failed to respond to invitation');
    }
  };

  const bg = isDark ? '#161618' : '#fff';
  const border = isDark ? '#2e2e33' : '#e5e7eb';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="modal-backdrop" onClick={onClose} />

      <div
        className="relative mx-4 flex flex-col"
        style={{
          width: '100%', maxWidth: 560, borderRadius: 12, overflow: 'hidden',
          background: bg, border: `1px solid ${border}`,
          boxShadow: '0 16px 48px rgba(0,0,0,0.22)',
          maxHeight: '80vh',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 16px', borderBottom: `1px solid ${border}`, flexShrink: 0,
        }}>
          {/* Select-all checkbox */}
          <input
            ref={selectAllRef}
            type="checkbox"
            checked={allSelected}
            onChange={toggleSelectAll}
            className="checkbox-brand flex-shrink-0 cursor-pointer"
          />

          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', flex: 1 }}>
            All Notifications
          </span>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Bulk delete — shown when items selected */}
            {hasSelection && (
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  fontSize: 12, fontWeight: 500, cursor: bulkDeleting ? 'default' : 'pointer',
                  color: '#ef4444',
                  background: isDark ? '#2d1b1b' : '#fef2f2',
                  border: `1px solid ${isDark ? '#5c2626' : '#fecaca'}`,
                  borderRadius: 6, padding: '4px 10px',
                  opacity: bulkDeleting ? 0.6 : 1,
                }}
              >
                {bulkDeleting
                  ? <Loader size={11} className="animate-spin" />
                  : <Trash2 size={11} />
                }
                Delete ({selected.size})
              </button>
            )}

            {/* Clear read — shown when no selection and there are read items */}
            {!hasSelection && hasRead && (
              <button
                onClick={handleClearRead}
                disabled={clearing}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  fontSize: 12, fontWeight: 500, cursor: clearing ? 'default' : 'pointer',
                  color: isDark ? '#a1a1aa' : '#6b7280',
                  background: isDark ? '#27272a' : '#f3f4f6',
                  border: `1px solid ${border}`,
                  borderRadius: 6, padding: '4px 10px',
                  opacity: clearing ? 0.6 : 1,
                }}
              >
                {clearing
                  ? <Loader size={11} className="animate-spin" />
                  : <Trash2 size={11} />
                }
                Clear read history
              </button>
            )}

            <button
              onClick={onClose}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, borderRadius: 6, cursor: 'pointer',
                background: 'none', border: 'none',
                color: isDark ? '#71717a' : '#9ca3af',
              }}
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Body */}
        <OsScroll style={{ flex: 1, minHeight: 0 }}>
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center" style={{ padding: '40px 32px' }}>
              <div
                className="flex items-center justify-center"
                style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(var(--accent-rgb), 0.12)', marginBottom: 12 }}
              >
                <Bell size={18} style={{ color: 'var(--accent)', opacity: 0.7 }} />
              </div>
              <p className="text-sm font-semibold text-gray-800 dark:text-white" style={{ margin: '0 0 3px' }}>No notifications yet</p>
              <p className="text-xs text-gray-400 dark:text-zinc-500" style={{ margin: 0 }}>Your notification history will appear here.</p>
            </div>
          ) : (
            notifications.map((n, i) => (
              <div
                key={n.id}
                onClick={() => toggleOne(n.id)}
                className={`px-4 py-3 flex items-center gap-3 transition-colors cursor-pointer ${
                  selected.has(n.id)
                    ? 'bg-violet-50/80 dark:bg-violet-950/30'
                    : !n.is_read
                      ? 'bg-violet-50/60 dark:bg-violet-950/20 hover:bg-violet-50 dark:hover:bg-violet-950/30'
                      : 'hover:bg-gray-50 dark:hover:bg-zinc-800/50'
                } ${i < notifications.length - 1 ? 'border-b border-gray-100 dark:border-zinc-800' : ''}`}
                style={{ borderLeft: !n.is_read ? '3px solid var(--accent)' : '3px solid transparent' }}
              >
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={selected.has(n.id)}
                  onChange={() => toggleOne(n.id)}
                  onClick={e => e.stopPropagation()}
                  className="checkbox-brand flex-shrink-0 cursor-pointer"
                  style={{ alignSelf: 'flex-start', marginTop: 6 }}
                />

                {/* Type icon */}
                <div style={{ flexShrink: 0, alignSelf: 'flex-start', paddingTop: 2 }}>
                  <NotifIcon type={n.type} />
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                      {n.title}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-zinc-500 shrink-0">
                      {formatTime(n.created_at)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-zinc-400 leading-relaxed" style={{ margin: 0 }}>{n.message}</p>

                  {/* Invite actions */}
                  {n.type === 'team_invitation' && (() => {
                    const invStatus = n.data?.invitation_status;
                    if (invStatus === 'accepted') {
                      return (
                        <div className="flex items-center gap-1.5 mt-1" onClick={e => e.stopPropagation()}>
                          <Check size={12} style={{ color: '#16a34a' }} />
                          <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 500 }}>Joined team</span>
                        </div>
                      );
                    }
                    if (invStatus === 'declined') {
                      return (
                        <div className="flex items-center gap-1.5 mt-1" onClick={e => e.stopPropagation()}>
                          <span style={{ fontSize: 11, color: isDark ? '#71717a' : '#9ca3af', fontWeight: 500 }}>Invitation declined</span>
                        </div>
                      );
                    }
                    return (
                      <div className="flex items-center gap-2 mt-2" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => handleInvite(n, 'decline')}
                          style={{
                            padding: '3px 12px', fontSize: 12, fontWeight: 500, borderRadius: 6, cursor: 'pointer',
                            border: `1px solid ${isDark ? '#52525b' : '#e5e7eb'}`,
                            background: isDark ? '#27272a' : '#f3f4f6',
                            color: isDark ? '#a1a1aa' : '#4b5563',
                          }}
                        >
                          Decline
                        </button>
                        <button
                          onClick={() => handleInvite(n, 'accept')}
                          className="px-3 py-1 text-xs font-semibold rounded-md text-white transition-colors"
                          style={{ background: 'var(--accent)' }}
                        >
                          Accept
                        </button>
                      </div>
                    );
                  })()}
                  {/* Appointment invite actions — the same shared component
                      the bell dropdown uses (single service path). */}
                  {n.type === 'appointment_invitation' && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <AppointmentInviteActions
                        notification={n}
                        isDark={isDark}
                        onResponded={(notification, status) => {
                          setNotifications(prev => prev.map(x =>
                            x.id === notification.id
                              ? { ...x, is_read: true, data: { ...x.data, invitation_status: status } }
                              : x
                          ));
                          if (!notification.is_read) setUnreadCount(prev => Math.max(0, prev - 1));
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </OsScroll>
      </div>
    </div>
  );
};

export default NotificationsModal;
